import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton } from '../../components';
import { ReasonNextData } from '../types';
import { Alert, CheckboxWithLabel, ChipGroup, SearchBoxRaw, StickyStackItem, useAlert } from '@uplift/ui-v2';
import { useSpecialties } from '@uplift/queries';
import { useL10n } from '@uplift/hooks';
import { array, bool, object, ObjectSchema } from 'yup';
import { Typography } from '@mui/material';
import { categorizeReasons, DisclaimerVariant, getDisclaimerVariants, ReasonCategoryKey } from './reason.config';

import { makeStyles } from 'tss-react/mui';

export type ReasonProps = {
  onNext: (data: ReasonNextData) => void;
  onBack: () => void;
  specialtyIds: string[];
  isBookingForSelf: boolean;
  banner?: React.ReactNode;
  hideDescription?: boolean;
  isRequired?: boolean;
};

type ReasonFormValues = {
  specialtyIds: string[];
  reasonNotHere: boolean;
};

const DISCLAIMER_COPY: Record<DisclaimerVariant, (l10n: ReturnType<typeof useL10n>) => string> = {
  stimulant: l10n => l10n.onboarding.reason.stimulantDisclaimer,
  benzodiazepine: l10n => l10n.onboarding.reason.benzodiazepineDisclaimer,
};

const CATEGORY_LABEL: Record<ReasonCategoryKey, (l10n: ReturnType<typeof useL10n>) => string> = {
  [ReasonCategoryKey.moodAndEmotions]: l10n => l10n.onboarding.reason.categories.moodAndEmotions,
  [ReasonCategoryKey.focusAndBehaviors]: l10n => l10n.onboarding.reason.categories.focusAndBehaviors,
  [ReasonCategoryKey.traumaAndFears]: l10n => l10n.onboarding.reason.categories.traumaAndFears,
  [ReasonCategoryKey.relationshipsAndIdentity]: l10n => l10n.onboarding.reason.categories.relationshipsAndIdentity,
  [ReasonCategoryKey.lifeChangesAndHealth]: l10n => l10n.onboarding.reason.categories.lifeChangesAndHealth,
  [ReasonCategoryKey.ongoingConditions]: l10n => l10n.onboarding.reason.categories.ongoingConditions,
  [ReasonCategoryKey.other]: l10n => l10n.onboarding.reason.categories.other,
};

export const Reason: React.VFC<ReasonProps> = props => {
  const { classes } = useReasonStyles();
  const l10n = useL10n();
  const alert = useAlert();
  const { data: reasons = [] } = useSpecialties();
  const [ search, setSearch ] = useState('');

  const filteredReasons = useMemo(() => {
    const query = search.trim().toLowerCase();

    return query ? reasons.filter(reason => reason.name.toLowerCase().includes(query)) : reasons;
  }, [ reasons, search ]);

  const categories = useMemo(() => categorizeReasons(filteredReasons), [ filteredReasons ]);

  return (
    <Formik
      initialValues={{ specialtyIds: props.specialtyIds, reasonNotHere: false }}
      onSubmit={props.onNext}
      validationSchema={props.isRequired ? validationSchema : undefined}
      validateOnMount
    >
      {formik => {
        const selectedReasons = reasons.filter(reason => formik.values.specialtyIds.includes(reason.id));
        const disclaimerVariants = getDisclaimerVariants(selectedReasons);

        return (
          <StepContainer>
            {props.banner}
            <StepContent>
              <StepHeading>{props.isBookingForSelf ? 'What brings you in?' : 'What brings them in?'}</StepHeading>
              {!props.hideDescription && <StepDescription>When we know what’s important to you{props.isBookingForSelf ? '' : ' and your loved ones'}, we can match you with providers who specialize in that area.</StepDescription>}
              <SearchBoxRaw
                className={classes.searchBox}
                clearable
                fullWidth
                placeholder={l10n.onboarding.reason.searchPlaceholder}
                ariaLabel={l10n.onboarding.reason.searchLabel}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {formik.values.specialtyIds.length > 0 && (
                <Typography variant="body2" color="textSecondary" className={classes.selectedCount}>
                  {l10n.onboarding.reason.selectedCount(formik.values.specialtyIds.length)}
                </Typography>
              )}
              <StepForm onSubmit={formik.handleSubmit}>
                {categories.length === 0 && (
                  <Typography color="textSecondary">{l10n.onboarding.reason.noSearchResults}</Typography>
                )}
                {categories.map(category => (
                  <div className={classes.checkboxSection} key={category.key}>
                    <Typography variant="overline" className={classes.checkboxSectionLabel}>{CATEGORY_LABEL[category.key](l10n)}</Typography>
                    <ChipGroup
                      name="specialtyIds"
                      showError={false}
                      options={category.reasons.map(reason => ({
                        label: reason.name,
                        value: reason.id
                      }))}
                    />
                  </div>
                ))}
                <div className={classes.checkboxSection}>
                  <CheckboxWithLabel
                    className={classes.checkboxGroup}
                    name="reasonNotHere"
                    color="secondary"
                    label="My reason isn't here"
                    disabled={formik.values.specialtyIds.length > 0}
                  />
                </div>
              </StepForm>
            </StepContent>
            {disclaimerVariants.length > 0 && (
              <StickyStackItem placement="bottom" order={1}>
                <div className={classes.disclaimerStack}>
                  <Alert flat severity="info" className={classes.disclaimer}>
                    {disclaimerVariants.length > 1 ? l10n.onboarding.reason.combinedDisclaimer : DISCLAIMER_COPY[disclaimerVariants[0]](l10n)}
                  </Alert>
                </div>
              </StickyStackItem>
            )}
            <StepNavigator onBack={props.onBack}>
              {/* Disabled MUI buttons have pointer-events: none, so this wrapper
                  receives the click and can explain why Next is unavailable. */}
              <span
                className={classes.nextWrapper}
                onClick={() => {
                  if (!formik.isValid) {
                    alert.warning(l10n.onboarding.reason.nextDisabledWarning);
                  }
                }}
              >
                <StepNextButton disabled={!formik.isValid} />
              </span>
            </StepNavigator>
          </StepContainer>
        );
      }}
    </Formik>
  );
};

const useReasonStyles = makeStyles({ name: 'Reason' })(theme => ({
  checkboxSection: {
    marginBottom: theme.spacing(3),
  },
  checkboxSectionLabel: {
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  checkboxGroup: {
    paddingLeft: theme.spacing(1.5)
  },
  searchBox: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  selectedCount: {
    marginBottom: theme.spacing(2),
  },
  disclaimerStack: {
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[4],
    width: '100%',
    boxSizing: 'border-box',
  },
  nextWrapper: {
    display: 'flex',
    '& > *': {
      flex: 1,
    },
  },
  disclaimer: {
    padding: theme.spacing(0.5, 2),
    borderRadius: 0,
  }
}));

const validationSchema = (): ObjectSchema<ReasonFormValues> => object({
  reasonNotHere: bool(),
  specialtyIds: array().when('reasonNotHere', {
    is: false,
    then: s => s.min(1, 'Please select at least one reason').required('Please select at least one reason'),
    otherwise: s => s.min(0).notRequired()
  })
});
